/**
 * BM25 (Best Matching 25) ranking algorithm implementation
 * Enhanced with weighted fields, geographical boosting, and freshness decay
 */

import { tokenize, tokenizeFields, createWeightedTokenMap } from './tokenizer.js';

// BM25 parameters - tuned for job search relevance
const BM25_K1 = 1.5;      // Term frequency saturation parameter (1.2-2.0 typical)
const BM25_B = 0.75;      // Length normalization parameter (0.5-0.8 typical)

/**
 * BM25 Index class for efficient document scoring
 */
export class BM25Index {
  constructor() {
    this.documents = new Map();     // Document storage
    this.termFrequencies = new Map(); // Term frequencies across all documents  
    this.documentLengths = new Map();  // Document lengths for normalization
    this.averageDocumentLength = 0;    // Average document length
    this.totalDocuments = 0;           // Total document count
    this.fieldWeights = {              // Default field weights for job search
      title: 3.0,
      companyName: 2.0, 
      locations: 2.0,
      tags: 2.0,
      skills: 2.0,
      category: 1.5,
      description: 1.0,
      requirements: 1.0
    };
  }

  /**
   * Sets custom field weights for scoring
   * @param {Object} weights - Field weight mappings
   */
  setFieldWeights(weights) {
    this.fieldWeights = { ...this.fieldWeights, ...weights };
  }

  /**
   * Adds a document to the index
   * @param {string} docId - Unique document identifier
   * @param {Object} document - Document with searchable fields
   * @param {Object} metadata - Additional metadata (location, date, etc.)
   */
  addDocument(docId, document, metadata = {}) {
    // Prepare searchable text fields
    const searchableFields = {
      title: document?.title || '',
      companyName: document?.companyName || document?.company || '',
      locations: this._extractLocationText(document?.locations),
      tags: Array.isArray(document?.tags) ? document?.tags?.join(' ') : (document?.tags || ''),
      skills: Array.isArray(document?.skills) ? document?.skills?.join(' ') : (document?.skills || ''),
      category: document?.category || document?.industry || '',
      description: document?.description || document?.descriptionHtml?.replace(/<[^>]*>/g, ' ') || '',
      requirements: Array.isArray(document?.requirements) 
        ? document?.requirements?.join(' ') 
        : (document?.requirements || '')
    };

    // Tokenize all fields
    const tokenizedFields = tokenizeFields(searchableFields, {
      title: { applyStemming: true, removeStopWords: true },
      companyName: { applyStemming: false, removeStopWords: true },
      locations: { applyStemming: false, removeStopWords: true },
      tags: { applyStemming: true, removeStopWords: false },
      skills: { applyStemming: true, removeStopWords: false },
      category: { applyStemming: true, removeStopWords: true },
      description: { applyStemming: true, removeStopWords: true },
      requirements: { applyStemming: true, removeStopWords: true }
    });

    // Create weighted token map
    const weightedTokens = createWeightedTokenMap(tokenizedFields, this.fieldWeights);

    // Calculate document length (sum of weighted token frequencies)
    const documentLength = Array.from(weightedTokens?.values())?.reduce((sum, freq) => sum + freq, 0);

    // Store document data
    this.documents?.set(docId, {
      ...document,
      ...metadata,
      tokenizedFields,
      weightedTokens,
      searchableFields
    });

    // Update index statistics
    this.documentLengths?.set(docId, documentLength);
    this.totalDocuments++;

    // Update term frequencies
    weightedTokens?.forEach((frequency, term) => {
      if (!this.termFrequencies?.has(term)) {
        this.termFrequencies?.set(term, new Map());
      }
      this.termFrequencies?.get(term)?.set(docId, frequency);
    });

    // Recalculate average document length
    this._updateAverageDocumentLength();
  }

  /**
   * Extracts searchable text from location data
   * @param {Array|string} locations - Location data
   * @returns {string} - Concatenated location text
   */
  _extractLocationText(locations) {
    if (!locations) return '';
    
    if (typeof locations === 'string') return locations;
    
    if (Array.isArray(locations)) {
      return locations?.map(loc => {
        if (typeof loc === 'string') return loc;
        if (typeof loc === 'object') {
          return [loc?.city, loc?.region, loc?.country]?.filter(Boolean)?.join(' ');
        }
        return '';
      })?.join(' ');
    }
    
    return '';
  }

  /**
   * Updates the average document length
   */
  _updateAverageDocumentLength() {
    if (this.totalDocuments === 0) {
      this.averageDocumentLength = 0;
      return;
    }

    const totalLength = Array.from(this.documentLengths?.values())?.reduce((sum, length) => sum + length, 0);
    this.averageDocumentLength = totalLength / this.totalDocuments;
  }

  /**
   * Calculates BM25 score for a document given query terms
   * @param {string} docId - Document ID
   * @param {Array<string>} queryTerms - Tokenized query terms
   * @returns {number} - BM25 score
   */
  _calculateBM25Score(docId, queryTerms) {
    const document = this.documents?.get(docId);
    if (!document) return 0;

    const documentLength = this.documentLengths?.get(docId) || 0;
    let score = 0;

    queryTerms?.forEach(term => {
      // Term frequency in document
      const termFreq = document?.weightedTokens?.get(term) || 0;
      if (termFreq === 0) return;

      // Document frequency (how many documents contain this term)
      const documentFreq = this.termFrequencies?.get(term)?.size || 1;
      
      // Inverse document frequency
      const idf = Math.log((this.totalDocuments - documentFreq + 0.5) / (documentFreq + 0.5));
      
      // BM25 formula
      const numerator = termFreq * (BM25_K1 + 1);
      const denominator = termFreq + BM25_K1 * (1 - BM25_B + BM25_B * (documentLength / this.averageDocumentLength));
      
      score += idf * (numerator / denominator);
    });

    return Math.max(0, score); // Ensure non-negative scores
  }

  /**
   * Applies geographical proximity boost to scores
   * @param {string} docId - Document ID
   * @param {Object} userLocation - User's location preference
   * @param {number} baseScore - Base BM25 score
   * @returns {number} - Boosted score
   */
  _applyGeoBoost(docId, userLocation, baseScore) {
    if (!userLocation?.city && !userLocation?.region) return baseScore;

    const document = this.documents?.get(docId);
    if (!document?.locations) return baseScore;

    let maxGeoBoost = 1.0; // No boost by default

    // Check each job location against user preference
    const jobLocations = Array.isArray(document?.locations) ? document?.locations : [document?.locations];
    
    jobLocations?.forEach(location => {
      let locationText = '';
      if (typeof location === 'string') {
        locationText = location?.toLowerCase();
      } else if (typeof location === 'object') {
        locationText = [location?.city, location?.region, location?.country]
          ?.filter(Boolean)
          ?.join(' ')
          ?.toLowerCase();
      }

      // Exact city match - highest boost
      if (userLocation?.city && locationText?.includes(userLocation?.city?.toLowerCase())) {
        maxGeoBoost = Math.max(maxGeoBoost, 1.8);
      }
      // State/region match - medium boost  
      else if (userLocation?.region && locationText?.includes(userLocation?.region?.toLowerCase())) {
        maxGeoBoost = Math.max(maxGeoBoost, 1.4);
      }
      // Country match - small boost
      else if (userLocation?.country && locationText?.includes(userLocation?.country?.toLowerCase())) {
        maxGeoBoost = Math.max(maxGeoBoost, 1.1);
      }
      // Remote work boost for any location preference
      else if (document?.workMode === 'REMOTE' || document?.workMode === 'remote') {
        maxGeoBoost = Math.max(maxGeoBoost, 1.2);
      }
    });

    return baseScore * maxGeoBoost;
  }

  /**
   * Applies time-based freshness decay to scores
   * @param {string} docId - Document ID
   * @param {number} baseScore - Base score before decay
   * @returns {number} - Score with freshness decay applied
   */
  _applyFreshnessDecay(docId, baseScore) {
    const document = this.documents?.get(docId);
    if (!document?.postedAt) return baseScore;

    const now = new Date();
    const postedDate = new Date(document?.postedAt);
    const daysSincePosted = Math.max(0, (now - postedDate) / (1000 * 60 * 60 * 24));

    // Exponential decay with half-life of 30 days
    const decayFactor = Math.exp(-0.023 * daysSincePosted); // ln(0.5)/30 ≈ -0.023
    
    // Apply more aggressive decay for very old jobs (>90 days)
    const agePenalty = daysSincePosted > 90 ? 0.5 : 1.0;
    
    return baseScore * decayFactor * agePenalty;
  }

  /**
   * Applies job-specific relevance boosts
   * @param {string} docId - Document ID  
   * @param {number} baseScore - Base score
   * @param {Object} queryContext - Additional query context
   * @returns {number} - Boosted score
   */
  _applyRelevanceBoosts(docId, baseScore, queryContext = {}) {
    const document = this.documents?.get(docId);
    if (!document) return baseScore;

    let boostedScore = baseScore;

    // Featured job boost
    if (document?.isFeatured) {
      boostedScore *= 1.3;
    }

    // Active job status boost
    if (document?.status === 'ACTIVE') {
      boostedScore *= 1.2;
    }

    // Experience level match boost
    if (queryContext?.experienceLevel && document?.experienceLevel === queryContext?.experienceLevel) {
      boostedScore *= 1.25;
    }

    // Work mode preference boost
    if (queryContext?.workMode && document?.workMode === queryContext?.workMode) {
      boostedScore *= 1.15;
    }

    // Employment type match boost
    if (queryContext?.employmentType && document?.employmentType === queryContext?.employmentType) {
      boostedScore *= 1.1;
    }

    // Salary range boost (prefer jobs with disclosed salaries)
    if (document?.salary?.min && document?.salary?.max) {
      boostedScore *= 1.05;
    }

    return boostedScore;
  }

  /**
   * Searches the index and returns ranked results
   * @param {string} query - Search query
   * @param {Object} options - Search options and filters
   * @returns {Array} - Ranked search results
   */
  search(query, options = {}) {
    const {
      limit = 50,
      offset = 0,
      userLocation = null,
      filters = {},
      queryContext = {},
      includeInactive = false
    } = options;

    // Tokenize query
    const queryTerms = tokenize(query, {
      applyStemming: true,
      removeStopWords: true,
      minLength: 2
    });

    if (queryTerms?.length === 0) {
      // Return all documents if no query terms, sorted by freshness
      return this._getAllDocuments(filters, includeInactive)
        ?.slice(offset, offset + limit);
    }

    // Score all documents
    const scores = new Map();
    
    this.documents?.forEach((document, docId) => {
      // Skip inactive jobs unless explicitly included
      if (!includeInactive && document?.status !== 'ACTIVE') return;

      // Apply filters first (early exit for performance)
      if (!this._passesFilters(document, filters)) return;

      // Calculate base BM25 score
      let score = this._calculateBM25Score(docId, queryTerms);
      
      if (score <= 0) return; // Skip documents with no term matches

      // Apply geographical boost
      if (userLocation) {
        score = this._applyGeoBoost(docId, userLocation, score);
      }

      // Apply freshness decay
      score = this._applyFreshnessDecay(docId, score);

      // Apply relevance boosts
      score = this._applyRelevanceBoosts(docId, score, queryContext);

      scores?.set(docId, score);
    });

    // Sort by score descending and return results
    return Array.from(scores?.entries())
      ?.sort(([, scoreA], [, scoreB]) => scoreB - scoreA)
      ?.slice(offset, offset + limit)
      ?.map(([docId, score]) => ({
        ...this.documents?.get(docId),
        _score: score,
        _matchedTerms: queryTerms?.filter(term => 
          this.documents?.get(docId)?.weightedTokens?.has(term)
        )
      }));
  }

  /**
   * Gets all documents with basic filtering and sorting
   * @param {Object} filters - Filter criteria
   * @param {boolean} includeInactive - Whether to include inactive jobs
   * @returns {Array} - Filtered and sorted documents
   */
  _getAllDocuments(filters, includeInactive) {
    const results = [];
    
    this.documents?.forEach((document, docId) => {
      if (!includeInactive && document?.status !== 'ACTIVE') return;
      if (!this._passesFilters(document, filters)) return;
      
      results?.push({
        ...document,
        _score: this._applyFreshnessDecay(docId, 1.0), // Use freshness as base score
        _matchedTerms: []
      });
    });

    // Sort by freshness score (newer first)
    return results?.sort((a, b) => b?._score - a?._score);
  }

  /**
   * Checks if document passes filter criteria
   * @param {Object} document - Document to check
   * @param {Object} filters - Filter criteria
   * @returns {boolean} - Whether document passes filters
   */
  _passesFilters(document, filters) {
    // Location filter
    if (filters?.location) {
      const locationMatch = this._checkLocationFilter(document, filters?.location);
      if (!locationMatch) return false;
    }

    // Work mode filter
    if (filters?.workMode && filters?.workMode !== 'ANY') {
      if (document?.workMode !== filters?.workMode) return false;
    }

    // Employment type filter
    if (filters?.employmentType && filters?.employmentType !== 'ANY') {
      if (document?.employmentType !== filters?.employmentType) return false;
    }

    // Experience level filter
    if (filters?.experienceLevel && filters?.experienceLevel !== 'ANY') {
      if (document?.experienceLevel !== filters?.experienceLevel) return false;
    }

    // Date posted filter
    if (filters?.datePosted && filters?.datePosted !== 'ANY') {
      const dateMatch = this._checkDateFilter(document, filters?.datePosted);
      if (!dateMatch) return false;
    }

    // Salary range filter
    if (filters?.salaryMin || filters?.salaryMax) {
      const salaryMatch = this._checkSalaryFilter(document, filters);
      if (!salaryMatch) return false;
    }

    return true;
  }

  /**
   * Checks location filter match
   * @param {Object} document - Document to check
   * @param {Object} locationFilter - Location filter criteria
   * @returns {boolean} - Whether location matches
   */
  _checkLocationFilter(document, locationFilter) {
    if (!document?.locations) return false;
    
    const jobLocations = Array.isArray(document?.locations) ? document?.locations : [document?.locations];
    
    return jobLocations?.some(location => {
      let locationText = '';
      if (typeof location === 'string') {
        locationText = location?.toLowerCase();
      } else if (typeof location === 'object') {
        locationText = [location?.city, location?.region, location?.country]
          ?.filter(Boolean)
          ?.join(' ')
          ?.toLowerCase();
      }

      // Check city match
      if (locationFilter?.city && 
          !locationText?.includes(locationFilter?.city?.toLowerCase())) {
        return false;
      }

      // Check region match  
      if (locationFilter?.region && 
          !locationText?.includes(locationFilter?.region?.toLowerCase())) {
        return false;
      }

      // Check country match
      if (locationFilter?.country && 
          !locationText?.includes(locationFilter?.country?.toLowerCase())) {
        return false;
      }

      return true;
    });
  }

  /**
   * Checks date posted filter
   * @param {Object} document - Document to check
   * @param {string} dateFilter - Date filter ('24H', '7D', '30D')
   * @returns {boolean} - Whether date matches filter
   */
  _checkDateFilter(document, dateFilter) {
    if (!document?.postedAt) return false;

    const now = new Date();
    const postedDate = new Date(document?.postedAt);
    const hoursSincePosted = (now - postedDate) / (1000 * 60 * 60);

    switch (dateFilter) {
      case '24H':
        return hoursSincePosted <= 24;
      case '7D':
        return hoursSincePosted <= 168; // 7 * 24
      case '30D':
        return hoursSincePosted <= 720; // 30 * 24
      default:
        return true;
    }
  }

  /**
   * Checks salary range filter
   * @param {Object} document - Document to check
   * @param {Object} filters - Filter object with salary criteria
   * @returns {boolean} - Whether salary matches filter
   */
  _checkSalaryFilter(document, filters) {
    if (!document?.salary?.min || !document?.salary?.max) return false;

    const jobMinSalary = document?.salary?.min;
    const jobMaxSalary = document?.salary?.max;

    if (filters?.salaryMin && jobMaxSalary < filters?.salaryMin) return false;
    if (filters?.salaryMax && jobMinSalary > filters?.salaryMax) return false;

    return true;
  }

  /**
   * Gets search statistics and debugging info
   * @returns {Object} - Index statistics
   */
  getStats() {
    return {
      totalDocuments: this.totalDocuments,
      averageDocumentLength: this.averageDocumentLength,
      uniqueTerms: this.termFrequencies?.size,
      fieldWeights: this.fieldWeights
    };
  }

  /**
   * Clears the entire index
   */
  clear() {
    this.documents?.clear();
    this.termFrequencies?.clear();
    this.documentLengths?.clear();
    this.averageDocumentLength = 0;
    this.totalDocuments = 0;
  }
}

export default BM25Index;